import { HookPayloads, type HookSource, type HookSourceInfo, HookSources, hookUrl, Ingest } from "@pagerless/schema";
import { type FormEvent, useState } from "react";
import { Link, useParams } from "react-router";
import { ScreenHead } from "../components/ScreenHead";
import { ErrorNote, Loading } from "../components/Shell";
import { useDeps, useResource } from "../context";
import { sourcesPager } from "../lib/pager";
import { ingestEndpoint } from "./Setup";

/**
 * Other tools: alerts from Prometheus, Grafana, Datadog, Sentry, uptime monitors and the rest come
 * into Pagerless and are grouped, escalated and closed like everything else. Make a key once, then
 * paste one URL per tool. The key is shown only on this visit (only its hash is kept).
 */

/** Where hook URLs point: the product's ingest host, or this server's /ingest when self-hosted. */
export function hookBase(origin: string): string {
  return ingestEndpoint(origin) ? `${origin}/ingest` : Ingest.baseUrl;
}

const GENERIC_PAYLOAD = `{"id": "disk-db1", "status": "firing", "title": "Disk almost full on db-1", "severity": "critical"}
{"id": "disk-db1", "status": "resolved"}`;

/** The one thing to do in the tool besides pasting the URL, and a snippet when the tool needs one. */
export function hookSteps(source: HookSource, url: string): { note: string; snippet?: string } {
  switch (source) {
    case "alertmanager":
      return {
        note: "Add a receiver and route your alerts to it.",
        snippet: `receivers:\n  - name: pagerless\n    webhook_configs:\n      - url: ${url}\n        send_resolved: true`,
      };
    case "grafana":
      return { note: "Optional HMAC secret: paste the same key." };
    case "datadog":
      return { note: "Use this payload. Then add @webhook-pagerless to each monitor.", snippet: HookPayloads.datadog };
    case "newrelic":
      return { note: "Keep the default payload template." };
    case "gcp":
      return { note: "Webhook channel, or a Pub/Sub push subscription to this URL." };
    case "azure":
      return { note: "Turn on the common alert schema." };
    case "sentry":
      return { note: "Turn on Alert Rule Action and Issue webhooks. Save the client secret below." };
    case "betterstack":
    case "pingdom":
      return { note: "Nothing else to set." };
    case "uptimerobot":
      return { note: "POST, send as JSON, with this value.", snippet: HookPayloads.uptimerobot };
    case "checkly":
      return { note: "Keep the default template. Optional secret: paste the same key." };
    case "stripe":
      return {
        note: "Events: payment_intent.payment_failed, payment_intent.succeeded, charge.failed. Save the signing secret below.",
      };
    case "github":
      return { note: "Content type JSON. Secret: paste the same key. Events: Workflow runs." };
    case "generic":
      return { note: "POST one alert per id. Same id with resolved closes it.", snippet: GENERIC_PAYLOAD };
  }
}

function Copy({ text, label }: { text: string; label: string }) {
  const { copy } = useDeps();
  const [s, setS] = useState<"idle" | "ok" | "fail">("idle");
  return (
    <>
      <button
        type="button"
        className="btn sm"
        onClick={() =>
          copy(text).then(
            () => setS("ok"),
            () => setS("fail"),
          )
        }
      >
        {s === "ok" ? "Copied" : label}
      </button>
      <span role="status" className="note" style={{ marginLeft: 8 }}>
        {s === "fail" ? "Couldn't copy. Select the text instead." : ""}
      </span>
    </>
  );
}

function SecretForm({ projectId, keyId, tool }: { projectId: string; keyId: string; tool: HookSourceInfo }) {
  const { api } = useDeps();
  const [secret, setSecret] = useState("");
  const [state, setState] = useState<{ s: "idle" | "saving" | "saved" } | { s: "error"; error: Error }>({ s: "idle" });
  async function save(e: FormEvent) {
    e.preventDefault();
    setState({ s: "saving" });
    try {
      await api.saveHookSecret(projectId, keyId, tool.source, secret.trim());
      setSecret("");
      setState({ s: "saved" });
    } catch (err) {
      setState({ s: "error", error: err instanceof Error ? err : new Error(String(err)) });
    }
  }
  return (
    <form onSubmit={save} className="btn-row" style={{ margin: "8px 0 0" }}>
      <label className="sr-only" htmlFor={`secret-${tool.source}`}>
        {tool.name} signing secret
      </label>
      <input
        id={`secret-${tool.source}`}
        type="password"
        autoComplete="off"
        placeholder={tool.source === "stripe" ? "whsec_…" : "Signing secret"}
        value={secret}
        onChange={(e) => setSecret(e.target.value)}
      />
      <button type="submit" className="btn sm" disabled={secret.trim().length < 8 || state.s === "saving"}>
        Save secret
      </button>
      <span role="status" className="note">
        {state.s === "saved" ? "Saved. Unsigned messages from it are now refused." : ""}
      </span>
      {state.s === "error" && <ErrorNote error={state.error} />}
    </form>
  );
}

export function Sources() {
  const { api } = useDeps();
  const { id = "" } = useParams();
  const projects = useResource((a) => a.projects());
  const [made, setMade] = useState<
    { s: "none" | "making" } | { s: "made"; key: string; keyId: string } | { s: "error"; error: Error }
  >({
    s: "none",
  });

  async function makeKey() {
    setMade({ s: "making" });
    try {
      const k = await api.createKey(id);
      setMade({ s: "made", key: k.key, keyId: k.id });
    } catch (err) {
      setMade({ s: "error", error: err instanceof Error ? err : new Error(String(err)) });
    }
  }

  if (projects.status === "loading") return <Loading what="Loading your app" />;
  if (projects.status === "error") return <ErrorNote error={projects.error} retry={projects.reload} />;
  const project = projects.data.find((p) => p.id === id);
  if (!project) return <ErrorNote error={new Error("We couldn't find this app.")} />;
  const base = hookBase(window.location.origin);

  return (
    <>
      <title>{`Other tools · ${project.name} · Pagerless`}</title>
      <ScreenHead pager={sourcesPager(made.s === "made", HookSources.length)} title={`Other tools for ${project.name}`}>
        <Link to="/apps" className="btn alt sm">
          Back to apps
        </Link>
      </ScreenHead>
      <p className="muted">
        Alerts from these tools open a problem here, wake or tell like the rest, and close when the tool says it is
        fine.
      </p>
      {made.s !== "made" ? (
        <div className="box pad">
          <p style={{ marginTop: 0 }}>Make a key for your tools. It shows once, inside each URL below.</p>
          <button type="button" className="btn" disabled={made.s === "making"} onClick={makeKey}>
            {made.s === "making" ? "Making the key…" : "Make a key"}
          </button>
          {made.s === "error" && <ErrorNote error={made.error} retry={makeKey} />}
        </div>
      ) : (
        <>
          <p className="note">Copy the URLs now. The key is not shown again; make a new one any time.</p>
          <ul className="box list" aria-label="Tools">
            {HookSources.map((tool) => {
              const url = hookUrl(tool.source, made.key, base);
              const steps = hookSteps(tool.source, url);
              return (
                <li key={tool.source} className="row" aria-label={tool.name} style={{ display: "block" }}>
                  <details>
                    <summary>
                      <b>{tool.name}</b> <span className="muted">· {tool.where}</span>
                    </summary>
                    <pre className="code">{url}</pre>
                    <Copy text={url} label="Copy URL" />
                    <p className="note">{steps.note}</p>
                    {steps.snippet && (
                      <>
                        <pre className="code">{steps.snippet}</pre>
                        <Copy text={steps.snippet} label="Copy" />
                      </>
                    )}
                    {tool.signing === "provider" && (
                      <SecretForm projectId={project.id} keyId={made.keyId} tool={tool} />
                    )}
                  </details>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </>
  );
}
